"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

import Footer from "../../components/layout/footer";
import Header from "../../components/layout/header";
import Image from "next/image";

import amsal from "../../assets/amsal-crop.jpeg";
import rani from "../../assets/rani.png";
import alfi from "../../assets/alfi.png";
import naufal from "../../assets/naufal.png";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const COLORS = {
  primary: "#003B5C",
  teal: "#00778B",
  gray: "#C7C9C7",
  gold: "#E3A130",
};

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

const DIRECTOR = {
  name: "Prof. Annas Bahtiar",
  title: "Director of Business Development",
  photo: amsal,
};

const TEAM = [
  {
    name: "M. Naufal Wiratama Azhari, S.H.",
    title: "Business Development Officer",
    photo: naufal,
  },
  {
    name: "Aulia Atrani, M.M",
    title: "Business Development Officer (Marketing and Cooperation)",
    photo: rani,
  },
  {
    name: "Alfi Febriany, S.E",
    title: "Business Development Officer (Finance)",
    photo: alfi,
  },
];

export default function HomePage() {
  const [darkMode, setDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem("darkMode") : null;
    const prefers =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial = saved !== null ? saved === "true" : prefers;
    setDarkMode(initial);
    if (typeof document !== "undefined")
      document.documentElement.classList.toggle("dark", initial);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode, mounted]);

  const [heroIndex, setHeroIndex] = useState(0);
  const heroIntervalRef = useRef(null);

  return (
    <div>
      <Header />

      {/* ================= ABOUT BUSINESS DEVELOPMENT ================= */}
     <section
  id="about-dpu"
  className="bg-white py-12 sm:py-16 border-t border-zinc-100"
>
  <div className="max-w-6xl mx-auto px-4 lg:px-0">
    <h2 className="text-2xl sm:text-3xl font-semibold text-[#003B5C] mb-4">
      About Business Development
    </h2>

    <p className="text-sm sm:text-base text-zinc-700 leading-relaxed text-justify">
      The establishment of the Directorate of Business Development (DPU)
      at Universitas Islam Internasional Indonesia (UIII) is mandated by
      the Government Regulation of the Republic of Indonesia Number 23 of
      2019 concerning the Statute of Universitas Islam Internasional
      Indonesia. The DPU serves as the executor of UIII&apos;s commercial
      development activities, with a vision of achieving financial
      independence as a State Higher Education Institution with
      Incorporated Legal Entity status (PTNBH).
    </p>

    <p className="mt-3 text-sm sm:text-base text-zinc-700 leading-relaxed text-justify">
      UIII&apos;s revenue sources include grants from the government,
      tuition fees, admission fees, business revenue, partnerships,
      endowment funds, and other sources. To enhance financial autonomy,
      strategic efforts are being pursued, including establishing
      collaborations with government entities, the private sector,
      non-profit organizations, and the public.
    </p>
  </div>
</section>


      {/* ================= THE TEAM ================= */}
      <section className="bg-white py-10 sm:py-14 border-t border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 lg:px-0">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#003B5C] mb-8">
            The Team
          </h2>

          {/* --- layout desktop --- */}
          <div className="hidden md:grid grid-cols-5 gap-y-16">
            {/* Row 1: Director di tengah */}
            <div className="col-span-5 flex justify-center">
              <div className="flex flex-col items-center text-center">
                <div className="w-40 h-40 mb-4">
                  <Image
                    src={amsal}
                    alt="Prof. Dr. Amsal Bakhtiar, MA."
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-base">
                  Prof. Dr. Amsal Bakhtiar, MA.
                </p>
                <p className="text-sm text-[#00558e]">
                  Director of Business Development
                </p>
              </div>
            </div>

            {/* Row 3: Naufal (kiri), Aulia (tengah), Alfi (kanan) */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={naufal}
                    alt="M. Naufal Wiratama Azhari, S.H."
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm text-center">
                  M. Naufal Wiratama Azhari, S.H.
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer 
                  <br/>
                  (MICE)
                </p>
              </div>
            </div>

            <div /> {/* col kosong */}

            <div className="flex justify-center">
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={rani}
                    alt="Aulia Atrani, M.M"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm">
                  Aulia Atrani, M.M
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer 
                  <br />
                  (Cooperation and Endowment)
                </p>
              </div>
            </div>

            <div /> {/* col kosong */}

            <div className="flex justify-center">
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={alfi}
                    alt="Alfi Febriany, S.E"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm">
                  Alfi Febriany, S.E
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer 
                  <br />
                  (Merchandise and Accommodation)
                </p>
              </div>
            </div>
          </div>

          {/* --- layout mobile: ditumpuk rapi --- */}
          <div className="md:hidden flex flex-col items-center gap-10 mt-4">
            {/* Director */}
            <div className="flex flex-col items-center text-center">
              <div className="w-40 h-40 mb-4">
                <Image
                  src={amsal}
                  alt="Prof. Annas Bahtiar"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="font-semibold text-[#00558e] text-base">
                  Prof. Dr. Amsal Bakhtiar, MA.
              </p>
              <p className="text-sm text-[#00558e]">
                Director of Business Development
              </p>
            </div>

            {/* Naufal */}
            <div className="flex flex-col items-center text-center gap-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={naufal}
                    alt="M. Naufal Wiratama Azhari, S.H."
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm text-center">
                  M. Naufal Wiratama Azhari, S.H.
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer
                  <br />
                  (MICE)
                </p>
              </div>

              {/* Aulia */}
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={rani}
                    alt="M. Naufal Wiratama Azhari, S.H."
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm">
                  Aulia Atrani, M.M
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer
                  <br />
                  (Cooperation and Endowment)
                </p>
              </div>

              {/* Alfi */}
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4">
                  <Image
                    src={alfi}
                    alt="Alfi Febriany, S.E"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-semibold text-[#00558e] text-sm">
                  Alfi Febriany, S.E
                </p>
                <p className="text-xs text-[#00558e] leading-snug">
                  Business Development Officer
                  <br />
                  (Merchandise and Accommodation)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= LOCATION AND HOURS ================= */}
      <section className="bg-zinc-50 py-12 sm:py-16 border-t border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 lg:px-0">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#003B5C] mb-6">
            Location and Hours
          </h2>

          <div className="grid md:grid-cols-[2fr,1fr] gap-6 items-start">
            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-sm bg-white">
              <iframe
                title="UIII DPU Location"
                src="https://www.google.com/maps?q=Universitas%20Islam%20Internasional%20Indonesia&output=embed"
                className="w-full h-80 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Info card */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-[#003B5C] mb-2">
                Our Office
              </h3>
              <p className="text-sm text-zinc-700 mb-4">
                UIII Rectorate Building, Level 2
                <br />
                Jl. Raya Bogor, KM 33, Cimanggis, Sukmajaya
                <br />
                Depok, West Java, Indonesia 16452
              </p>

              <div className="mb-4">
                <p className="text-sm font-medium text-zinc-800">
                  Office Hours
                </p>
                <p className="text-sm text-zinc-700">
                  Monday – Friday: 08.00 – 16.00 (WIB)
                  <br />
                  Closed on weekends and public holidays.
                </p>
              </div>

              <button
                className="mt-2 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-full bg-[#003B5C] text-white hover:bg-[#00263c]"
                onClick={() =>
                  window.open(
                    "https://maps.google.com/?q=Universitas+Islam+Internasional+Indonesia",
                    "_blank"
                  )
                }
              >
                Directions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <Footer />
    </div>
  );
}
